/**
 * Allys Property Search Bar
 * 
 * Theme-specific wrapper for the core PropertySearchBar component.
 * Adds Allys styling and sticky scroll behavior.
 */

'use client';

import type { PropertySearchValues } from '@/components/properties/PropertySearchBar';
import { PropertySearchBar as CorePropertySearchBar } from '@/components/properties/PropertySearchBar';
import { motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';

interface AllysPropertySearchBarProps {
  onSearch?: (values: PropertySearchValues) => void;
  /**
   * Enable sticky behavior - sticks below nav when scrolling
   */
  sticky?: boolean;
  /**
   * ID of hero section to track for sticky behavior
   */
  heroSectionId?: string;
  /**
   * ID of section after which search should hide
   */
  hideAfterSectionId?: string;
  /**
   * Current locale for translations
   */
  locale?: 'en' | 'es' | 'fr';
  /**
   * Messages for translations
   */
  messages?: Record<string, unknown>;
}

// Helper to safely get string from messages
const safeGetString = (obj: unknown, defaultValue: string): string => {
  if (typeof obj === 'string') return obj;
  return defaultValue;
};

/**
 * Allys Property Search Bar Component
 * 
 * Wraps CorePropertySearchBar with Allys theme styling and optional sticky behavior
 */
export function AllysPropertySearchBar({ 
  onSearch,
  sticky = true,
  heroSectionId = 'hero-section',
  hideAfterSectionId = 'content-after-search',
  locale: _locale = 'en',
  messages
}: AllysPropertySearchBarProps) {
  const [isSticky, setIsSticky] = useState(false);
  const [shouldHide, setShouldHide] = useState(false);
  const searchBarRef = useRef<HTMLDivElement>(null);

  // Get translations from messages
  const propertySearch = messages?.propertySearch as Record<string, unknown> | undefined;
  const searchLabels = propertySearch?.labels as Record<string, unknown> | undefined;
  
  // Build translated labels
  const labels = {
    location: safeGetString(searchLabels?.location, 'Location'),
    type: safeGetString(searchLabels?.type, 'Type'),
    price: safeGetString(searchLabels?.price, 'Price'),
    bedrooms: safeGetString(searchLabels?.bedrooms, 'Beds'),
    searchButton: safeGetString(searchLabels?.searchButton, 'Search'),
  };

  // Sticky scroll behavior
  useEffect(() => {
    if (!sticky) return;

    const handleScroll = () => {
      const heroSection = document.getElementById(heroSectionId);
      const hideAfterSection = document.getElementById(hideAfterSectionId);
      
      if (!heroSection) return;

      const heroRect = heroSection.getBoundingClientRect();
      const navHeight = 88; // Navigation height
      const searchBarHeight = searchBarRef.current?.offsetHeight || 100;
      
      // Make sticky when we've scrolled past 60% of hero
      const scrollProgress = 1 - (heroRect.bottom / heroRect.height);
      const shouldBeSticky = scrollProgress > 0.6;
      setIsSticky(shouldBeSticky);

      // Hide when we've scrolled past the BOTTOM of the hideAfterSection
      if (hideAfterSection && shouldBeSticky) {
        const hideAfterSectionRect = hideAfterSection.getBoundingClientRect();
        // Only hide when the BOTTOM of the section has scrolled past the search bar position
        setShouldHide(hideAfterSectionRect.bottom <= navHeight + searchBarHeight);
      } else {
        setShouldHide(false);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, [sticky, heroSectionId, hideAfterSectionId]);

  // Shared styling for both search bar instances
  const searchBarClassName = `
    /* Form Container */
    p-2
    
    /* Fields Container */
    [&_.property-search-fields]:flex
    [&_.property-search-fields]:items-center
    [&_.property-search-fields]:flex-wrap
    [&_.property-search-fields]:gap-1
    
    /* Field Styling */
    [&_.property-search-field]:flex-1
    [&_.property-search-field]:px-5
    [&_.property-search-field]:lg:px-6
    [&_.property-search-field]:py-3
    [&_.property-search-field]:lg:py-3.5
    [&_.property-search-field]:border-r
    [&_.property-search-field]:border-[var(--color-border)]
    [&_.property-search-field]:relative
    [&_.property-search-field]:min-w-[100px]
    [&_.property-search-field[data-field="location"]]:min-w-[180px]
    [&_.property-search-field[data-field="type"]]:min-w-[120px]
    [&_.property-search-field[data-field="price"]]:min-w-[120px]
    [&_.property-search-field:last-of-type]:border-r-0
    
    /* Labels */
    [&_.property-search-label]:block
    [&_.property-search-label]:text-xs
    [&_.property-search-label]:font-medium
    [&_.property-search-label]:text-[var(--color-text-muted)]
    [&_.property-search-label]:mb-1.5
    [&_.property-search-label]:[font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif]
    
    /* Input Wrapper */
    [&_.property-search-input-wrapper]:flex
    [&_.property-search-input-wrapper]:items-center
    [&_.property-search-input-wrapper]:gap-2.5
    
    /* Icons */
    [&_.property-search-icon]:w-4
    [&_.property-search-icon]:h-4
    [&_.property-search-icon]:text-[var(--color-primary)]
    [&_.property-search-icon]:flex-shrink-0
    
    /* Input Fields */
    [&_.property-search-input]:w-full
    [&_.property-search-input]:text-[var(--color-text)]
    [&_.property-search-input]:placeholder-[var(--color-text-muted)]
    [&_.property-search-input]:focus:outline-none
    [&_.property-search-input]:text-sm
    [&_.property-search-input]:bg-transparent
    [&_.property-search-input]:font-medium
    [&_.property-search-input]:[font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif]
    
    /* Select Fields */
    [&_.property-search-select]:w-full
    [&_.property-search-select]:text-[var(--color-text)]
    [&_.property-search-select]:focus:outline-none
    [&_.property-search-select]:text-sm
    [&_.property-search-select]:bg-transparent
    [&_.property-search-select]:appearance-none
    [&_.property-search-select]:cursor-pointer
    [&_.property-search-select]:pr-6
    [&_.property-search-select]:font-medium
    [&_.property-search-select]:[font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif]
    
    /* Dropdown Icons */
    [&_.property-search-dropdown-icon]:w-3.5
    [&_.property-search-dropdown-icon]:h-3.5
    [&_.property-search-dropdown-icon]:absolute
    [&_.property-search-dropdown-icon]:right-5
    [&_.property-search-dropdown-icon]:lg:right-6
    [&_.property-search-dropdown-icon]:top-1/2
    [&_.property-search-dropdown-icon]:-translate-y-1/2
    [&_.property-search-dropdown-icon]:pointer-events-none
    [&_.property-search-dropdown-icon]:text-[var(--color-text-muted)]
    
    /* Button Container */
    [&_.property-search-button-container]:px-3
    [&_.property-search-button-container]:py-3
    [&_.property-search-button-container]:lg:py-3.5
    [&_.property-search-button-container]:relative
    
    /* Gradient Button Styling */
    [&_.gradient-button]:relative
    [&_.gradient-button]:overflow-hidden
    [&_.gradient-button]:h-full
    [&_.gradient-button]:px-6
    [&_.gradient-button]:lg:px-8
    [&_.gradient-button]:py-2.5
    [&_.gradient-button]:lg:py-3
    [&_.gradient-button]:bg-gradient-to-r
    [&_.gradient-button]:from-[var(--color-primary)]
    [&_.gradient-button]:to-[var(--color-accent)]
    [&_.gradient-button]:rounded-full
    [&_.gradient-button]:font-semibold
    [&_.gradient-button]:text-white
    [&_.gradient-button]:text-sm
    [&_.gradient-button]:shadow-lg
    [&_.gradient-button]:shadow-[var(--color-primary)]/20
    [&_.gradient-button]:hover:shadow-xl
    [&_.gradient-button]:hover:shadow-[var(--color-primary)]/30
    [&_.gradient-button]:transition-all
    [&_.gradient-button]:duration-300
    [&_.gradient-button]:[font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif]
    
    /* Button Shine Effect */
    [&_.gradient-button-shine]:absolute
    [&_.gradient-button-shine]:inset-0
    [&_.gradient-button-shine]:bg-gradient-to-r
    [&_.gradient-button-shine]:from-transparent
    [&_.gradient-button-shine]:via-white/20
    [&_.gradient-button-shine]:to-transparent
    [&_.gradient-button-shine]:-translate-x-full
    [&_.gradient-button-shine]:group-hover:translate-x-full
    [&_.gradient-button-shine]:transition-transform
    [&_.gradient-button-shine]:duration-700
    [&_.gradient-button-shine]:ease-out
    [&_.gradient-button]:group
    
    /* Button Content */
    [&_.gradient-button-content]:relative
    [&_.gradient-button-content]:z-10
    [&_.gradient-button-content]:flex
    [&_.gradient-button-content]:items-center
    [&_.gradient-button-content]:justify-center
    [&_.gradient-button-content]:gap-2
    
    /* Button Icon */
    [&_.gradient-button-icon]:flex
    [&_.gradient-button-icon]:items-center
    [&_.gradient-button-icon]:justify-center
    
    [&_.gradient-button-icon>svg]:w-4
    [&_.gradient-button-icon>svg]:h-4
    
    /* Button Text */
    [&_.gradient-button-text]:inline
  `;

  return (
    <>
      {/* Initial Search Bar - Rendered in hero via HeroWithSearch wrapper */}
      <motion.div
        ref={searchBarRef}
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: isSticky ? 0 : 1, y: isSticky ? 40 : 0 }}
        transition={{ duration: isSticky ? 0.2 : 1, delay: isSticky ? 0 : 0.9, ease: [0.25, 0.46, 0.45, 0.94] }}
        className={isSticky ? 'pointer-events-none invisible absolute' : ''}
      >
        <CorePropertySearchBar
          {...(onSearch ? { onSearch } : {})}
          labels={labels}
          {...(messages && { messages })}
          {...(_locale && { locale: _locale })}
          renderWrapper={({ children, isFocused }) => (
            <div className={`
              relative bg-white/95 backdrop-blur-2xl rounded-full shadow-2xl 
              transition-all duration-300 overflow-hidden
              border border-white/40
              ${isFocused ? 'shadow-[0_25px_80px_rgba(0,71,171,0.15)] ring-2 ring-[var(--color-primary)]/30 scale-[1.01]' : 'shadow-[0_15px_50px_rgba(0,0,0,0.15)]'}
            `}>
              {children}
            </div>
          )}
          className={searchBarClassName}
        />
      </motion.div>

      {/* Sticky Search Bar - Fixed below navigation */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: (isSticky && !shouldHide) ? 1 : 0, y: (isSticky && !shouldHide) ? 0 : -20 }}
        transition={{ duration: 0.3 }}
        className={`fixed top-[88px] left-0 right-0 z-[45] ${(!isSticky || shouldHide) ? 'pointer-events-none' : ''}`}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <CorePropertySearchBar
            {...(onSearch ? { onSearch } : {})}
            labels={labels}
            {...(messages && { messages })}
            {...(_locale && { locale: _locale })}
            renderWrapper={({ children, isFocused }) => (
              <div className={`
                relative bg-white backdrop-blur-xl rounded-full shadow-lg 
                transition-all duration-300 overflow-hidden
                border border-gray-200/70
                ${isFocused ? 'shadow-xl shadow-[var(--color-primary)]/10 ring-2 ring-[var(--color-primary)]/20' : ''}
              `}>
                {children}
              </div>
            )}
            className={searchBarClassName}
          />
        </div>
      </motion.div>
    </>
  );
}

